import { useEffect, useRef } from 'react';
import { unstable_serialize, useSWRConfig } from 'swr';
import type { Key, Middleware, SWRConfiguration } from 'swr';
import {
  unstable_serialize as unstableSerializeInfinite,
  type SWRInfiniteKeyLoader,
} from 'swr/infinite';

type ShouldRetryOnError = SWRConfiguration['shouldRetryOnError'];

export interface SwrRetryConfig {
  shouldRetryOnError?: ShouldRetryOnError;
  errorRetryCount?: number;
}

/**
 * Whether SWR's own error-retry timer will retry this error. Hooks pass
 * shouldRetryOnError as a boolean or as a predicate on the error (e.g. retry
 * only canceled requests); SWR's default is true.
 */
export function swrRetriesError(
  shouldRetryOnError: ShouldRetryOnError,
  error: unknown
): boolean {
  if (typeof shouldRetryOnError === 'function') {
    return shouldRetryOnError(error);
  }
  return shouldRetryOnError !== false;
}

/**
 * Whether SWR's retry timer is guaranteed to recover this cached error. With a
 * finite errorRetryCount the timer stops after that many attempts and the
 * cache cannot say whether they are used up, so only unlimited retries count.
 */
export function swrRecoversError(
  { shouldRetryOnError, errorRetryCount }: SwrRetryConfig,
  error: unknown
): boolean {
  return (
    errorRetryCount === undefined && swrRetriesError(shouldRetryOnError, error)
  );
}

/**
 * SWR skips a mount's initial revalidation when another mounted reader of the
 * same key holds a cached error. When SWR's retry timer will not recover that
 * error (and focus / reconnect revalidation is off globally) nothing else
 * refetches, so a screen that mounts while the previous one is still
 * committed (tab switch, route change) would keep the error forever. This
 * forces one request for that mount. Errors SWR keeps retrying without a cap
 * are left to its own timer, since a forced fetch there would start a second,
 * unbounded retry loop. With a capped retry count a forced fetch can overlap a
 * retry still in progress; it adds at most 1 + errorRetryCount requests (the
 * forced fetch and the capped retry chain a failure of it starts).
 *
 * `cacheKey` is the exact cache key the error lives under. Checks once per key
 * activation and reads the cache rather than this render's values: when SWR
 * does revalidate on mount it marks the entry validating in a layout effect,
 * which runs before this effect, so no duplicate request is sent.
 */
function useRevalidateCachedErrorOnMount(
  cacheKey: string | null,
  mutate: () => Promise<unknown>,
  retryConfig: SwrRetryConfig
): void {
  const { cache } = useSWRConfig();
  const checkedKeyRef = useRef<string | null>(null);
  // Predicates are often inline arrows; read the latest without re-running.
  const retryConfigRef = useRef(retryConfig);
  retryConfigRef.current = retryConfig;
  useEffect(() => {
    if (!cacheKey) {
      checkedKeyRef.current = null;
      return;
    }
    if (checkedKeyRef.current === cacheKey) return;
    checkedKeyRef.current = cacheKey;
    const cached = cache.get(cacheKey);
    if (
      cached?.error !== undefined &&
      !cached.isValidating &&
      !swrRecoversError(retryConfigRef.current, cached.error)
    ) {
      // Bound mutate() with no data revalidates, bypassing request dedupe.
      // SWR records a failure in the cache; the promise itself is not needed.
      mutate().catch(() => {
        // swallow-ok: SWR stores the revalidation failure in its error cache; consuming hooks still receive that error and this middleware only prevents an unhandled retry promise.
      });
    }
  }, [cacheKey, cache, mutate]);
}

const noopRevalidate = (): Promise<undefined> => Promise.resolve(undefined);

// useSWRInfinite hands middleware its getKey function as the key, and a plain
// key function takes no arguments, so a function key on an infinite hook is
// its getKey loader.
const isInfiniteKeyLoader = (key: Key): key is SWRInfiniteKeyLoader =>
  typeof key === 'function';

const hasSetSize = (response: object): boolean =>
  'setSize' in response && typeof response.setSize === 'function';

/**
 * The cache key a hook's error lives under. useSWRInfinite hands middleware
 * its getKey function and keeps the error under the infinite key derived from
 * the first page, so its result (which carries setSize) is looked up there.
 */
function errorCacheKey(key: Key, infinite: boolean): string | null {
  if (!key) return null;
  try {
    let serialized: string;
    if (!infinite) {
      serialized = unstable_serialize(key);
    } else if (isInfiniteKeyLoader(key)) {
      serialized = unstableSerializeInfinite(key);
    } else {
      return null;
    }
    return serialized || null;
  } catch {
    // swallow-ok: SWR key-loader rejection means dependencies are not ready; there is no serialized cache key to recover until a later render supplies one.
    // A getKey that is not ready yet has no cache entry to recover.
    return null;
  }
}

/**
 * SWR middleware form of useRevalidateCachedErrorOnMount. Registered once in
 * swrGlobalConfig (api/swrConfig.ts), so every hook under the app's root
 * SWRConfig gets it, useSWRInfinite included. It stays a no-op unless a mount
 * lands on a cached error that nothing is fetching and that SWR's retry timer
 * will not recover (shouldRetryOnError false or a predicate that rejects the
 * error, or a capped errorRetryCount). It also skips hooks that opt out of
 * mount revalidation (revalidateOnMount: false) and results without a bound
 * mutate (useSWRMutation).
 */
export const revalidateCachedErrorOnMount: Middleware = (useSWRNext) =>
  function useSWRWithCachedErrorRecovery(key, fetcher, config) {
    const swr = useSWRNext(key, fetcher, config);
    // An infinite hook's bare mutate() refetches every loaded page, not just
    // the failed one. That only happens on a mount over a cached error, and
    // earlier pages staying cached is exactly when a later page's error is
    // otherwise stranded, so infinite hooks with partial data are included.
    const canRevalidate =
      config.revalidateOnMount !== false && typeof swr.mutate === 'function';
    useRevalidateCachedErrorOnMount(
      canRevalidate ? errorCacheKey(key, hasSetSize(swr)) : null,
      canRevalidate ? swr.mutate : noopRevalidate,
      {
        shouldRetryOnError: config.shouldRetryOnError,
        errorRetryCount: config.errorRetryCount,
      }
    );
    return swr;
  };
